// src/Components/ProductCard/ProductCard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaRegFileAlt, FaRegClock,  } from 'react-icons/fa';
import { FaHeart } from 'react-icons/fa';
import { FiShare2 } from 'react-icons/fi';
import { useCart } from '../../contexts/CartContext';
import './ProductCard.css';

const ProductCard = ({ product }) => {
  const {
    id,
    name,
    image,
    price,
    oldPrice,
    badge,
    category,
    stock,
    manufacturer
  } = product;

  const { addProductToCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Fallback image if product image is not available
  const fallbackImage = 'https://i.pinimg.com/736x/df/12/4a/df124abf279de0e5291891691e1c9bed.jpg';
  
  const backgroundImage = imageError || !image ? fallbackImage : image;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await addProductToCart(id, 1);
      setTimeout(() => {
        setLoading(false);
      }, 1000); // Add a small delay to show feedback
    } catch (error) {
      console.error('Error adding product to cart:', error);
      setLoading(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <motion.div 
      className="product-card"
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {/* Product image with hover actions */}
      <div 
        className="product-image-container"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <Link to={`/product/${id}`} className="product-image-link">
          {/* Hidden image to detect loading errors */}
          <img 
            src={image} 
            alt=""
            style={{ display: 'none' }}
            onError={handleImageError}
          />
          
          {/* Badge with modern styling */}
          {badge && (
            <span className={`product-badge ${badge.toLowerCase()}`}>
              {badge === "SALE" && oldPrice ? (
                `-${Math.round((1 - price/oldPrice) * 100)}%`
              ) : badge}
            </span>
          )}

          {/* Quick actions overlay */}
          <div className="product-actions-overlay">
            <button className="action-btn wishlist-btn">
              <FaHeart />
            </button>
            <button className="action-btn share-btn">
              <FiShare2 />
            </button>
          </div>

          {stock <= 0 && (
            <div className="out-of-stock-overlay">
              <span>Currently Unavailable</span>
            </div>
          )}
        </Link>
      </div>

      <div className="product-details">
        {/* Manufacturer and category */}
        <div className="product-meta">
          <span className="manufacturer">
            {manufacturer || 'PrimeCare'}
          </span>
          <span className="product-category">{category}</span>
        </div>

        {/* Product name */}
        <h3 className="product-name">
          <Link to={`/product/${id}`}>{name}</Link>
        </h3>

        {/* Pricing */}
        <div className="product-pricing">
          <div className="price-container">
            <span className="current-price">€{price.toFixed(2)}</span>
            {oldPrice && (
              <>
                <span className="old-price">€{oldPrice.toFixed(2)}</span>
                <span className="price-savings">
                  Save €{(oldPrice - price).toFixed(2)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="product-cta">
          {stock > 0 ? (
            <>
              <motion.button 
                className="btns add-to-cart"
                onClick={handleAddToCart}
                whileTap={{ scale: 0.95 }}
                disabled={loading}
              >
                <FaShoppingCart className="btn-icon" />
                <span>{loading ? 'Adding...' : 'Add to Cart'}</span>
              </motion.button>
              <Link
                to={`/quote?product=${id}`}
                className="btns btn-quote"
              >
                <FaRegFileAlt className="btn-icon" />
                <span>Quote</span>
              </Link>
            </>
          ) : (
            <>
              <button className="btn notify-me">
                <FaRegClock className="btn-icon" />
                <span>Notify Me</span>
              </button>
              <Link
                to={`/quote?product=${id}`}
                className="btn btn-quote"
              >
                <FaRegFileAlt className="btn-icon" />
                <span>Quote</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;